import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import './EditableForm.css';
import { fetchAPI, postAPI } from '../../../utils/utils';
import { formatMoney } from '../../../utils/money';
import {
  createSubmitFormValues,
  getRequiredVisibleElements,
} from '../utils/formVisibility';
import useConditionalFormState from './useConditionalFormState';
import { isAutoPaymentMethod, webPaymentMethodOptions } from '../utils/autoPaymentMethod';

const preprocessHTML = (html = '') => {
  const urlRegex = /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])/g;
  const boldRegex = /(\*)(.*?)\1/g;
  const emphasisRegex = /(_)(.*?)\1/g;
  // Safari-safe @username regex (no lookbehind). Captures optional prefix and username
  const usernameRegex = /(^|[^\w.])@([\w]+(?:\.[\w]+)*)\b/g;
  const emailRegex = /(\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b)/g;

  return String(html || '')
    .replace(/\n/g, '<br />')
    .split(/(<a\b[^>]*>.*?<\/a>)/gi)
    .map((part, index) => {
      if (index % 2 === 0) {
        return part
          .replace(urlRegex, (match) => `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`)
          .replace(boldRegex, (match, _p1, p2) => `<b>${p2}</b>`)
          .replace(emphasisRegex, (match, _p1, p2) => `<em>${p2}</em>`)
          .replace(usernameRegex, (match, prefix, username) => `${prefix}<a href="https://www.instagram.com/${username}/" target="_blank" rel="noopener noreferrer">@${username}</a>`)
          .replace(emailRegex, (match) => `<a href="mailto:${match}">${match}</a>`);
      }
      return part;
    })
    .join('');
};

const formatPriceDisplay = (amount) => {
  const numericAmount = Number(amount);
  if (!isFinite(numericAmount)) return `${amount}€`;
  return numericAmount === 0 ? 'Gratuït' : `${formatMoney(numericAmount)}€`;
};

const EditableForm = ({
  isLogged,
  mote,
  setMote,
  castellersInfo,
  formData,
  evId,
  user,
  socket,
  formValues,
  formValuesLoaded = true,
  setFormValues,
  formSubmitted,
}) => {
  const outdated = formData?.closingDate && moment(formData?.closingDate).isBefore(moment());
  const closeToEnding = !outdated && formData?.closingDate && moment(formData?.closingDate).isBefore(moment().add(2, 'day'));
  const notOpenYet = formData?.openingDate && moment(formData?.openingDate).isAfter(moment());
  const formDisabled = outdated || notOpenYet || formData?.hidden;

  const [sent, setSent] = useState(false);
  const [copiedText, setCopiedText] = useState({});
  const [uploadState, setUploadState] = useState({});
  const [formImagesValues, setFormImagesValues] = useState({});
  const [staticImages, setStaticImages] = useState({});
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWarningAccepted, setIsWarningAccepted] = useState(false);
  const [remainingTickets, setRemainingTickets] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(isLogged && user in castellersInfo);

  const elements = useMemo(
    () => (formData?.elements || []).filter((element) => !element.deleted),
    [formData?.elements]
  );

  useEffect(() => {
    setIsLoggedIn(Boolean(isLogged && user && castellersInfo?.[user]));
  }, [castellersInfo, isLogged, user]);

  const {
    canApplyImage,
    isElementVisible,
    sanitizeValues,
    shouldRequestImage,
    totalPriceToPay,
    visibleTicketElements: visibleFormTickets,
  } = useConditionalFormState({
    elements,
    formData,
    formValues,
    formValuesLoaded,
    scopeKey: `${evId}:${user}`,
    setFormImagesValues,
    setFormValues,
    setUploadState,
  });

  const userInfo = isLoggedIn ? (castellersInfo?.[user] || {}) : {};

  const formatText = (rawText) => {
    const nomValue = userInfo?.nom || '';
    const cognomValue = userInfo?.cognom || userInfo?.['primer-cognom'] || '';
    const sobrenomValue = isLoggedIn ? (userInfo?.mote || userInfo?.nom || '') : (mote || '');

    return String(rawText || '')
      .replaceAll('{nom}', nomValue)
      .replaceAll('{cognom}', cognomValue)
      .replaceAll('{sobrenom}', sobrenomValue)
      .replaceAll('{mote}', sobrenomValue);
  };

  const formatRichText = (text) => preprocessHTML(formatText(text));

  useEffect(() => {
    if (sent) return;
    elements.forEach((element) => {
      const { id, type } = element;
      if (type === 'image-upload') {
        if (!shouldRequestImage(element)) return;
        if (formImagesValues[id]) return;
        if (!isLoggedIn && formSubmitted === false) return;
        fetchAPI(`/form_image_url/${evId}/${id}/${user}`, ({ url }) => {
          if (!url) return;
          fetch(url)
            .then((res) => {
              if (!res.ok) throw new Error('Error fetching image from S3');
              return res.text();
            })
            .then((data) => {
              if (!canApplyImage(element)) return;
              setFormImagesValues((prev) => ({ ...prev, [id]: data }));
              setUploadState((prev) => ({ ...prev, [id]: 'uploaded' }));
            })
            .catch(() => {});
        }, false, false);
      } else if (type === 'image') {
        if (staticImages[id]) return;
        fetchAPI(`/form_image_url/${evId}/${id}`, ({ url }) => {
          if (!url) return;
          fetch(url)
            .then((res) => {
              if (!res.ok) throw new Error('Error fetching image from S3');
              return res.text();
            })
            .then((data) => {
              setStaticImages((prev) => ({ ...prev, [id]: data }));
            })
            .catch(() => {});
        }, false, false);
      }
    });
  }, [canApplyImage, elements, evId, formImagesValues, formSubmitted, isLoggedIn, sent, shouldRequestImage, staticImages, user]);

  useEffect(() => {
    if (sent) return;

    const updateRemainingTickets = (optionId, nextRemaining) => {
      setRemainingTickets((prev) => {
        if (prev?.[optionId] === nextRemaining) return prev;
        return {
          ...prev,
          [optionId]: nextRemaining,
        };
      });
    };

    const ticketElements = elements.filter((element) => ['ticket', 'ticket-v2', 'ticket-v2-single'].includes(element.type));
    ticketElements.forEach((element) => {
      if (element.type === 'ticket') {
        if (!element.limit) return;
        const optionId = `${element.id}_0`;
        postAPI('/total_sold', {
          elementId: element.id,
          optionIndex: null,
          castellerId: user,
        }, (data) => {
          const totalSold = data?.total_sold || 0;
          const limit = element.limit || Infinity;
          updateRemainingTickets(optionId, limit - totalSold - (formValues?.[element.id] === 0 ? 1 : 0));
        });
      } else {
        element.content.options.forEach((option, index) => {
          if (option.hidden) return;
          if (!option.limit) return;
          const optionId = `${element.id}_${index}`;
          postAPI('/total_sold', {
            elementId: element.id,
            optionIndex: index,
            castellerId: user,
          }, (data) => {
            const totalSold = data?.total_sold || 0;
            const limit = option.limit || Infinity;
            updateRemainingTickets(optionId, limit - totalSold - (formValues?.[element.id]?.[index] || 0));
          });
        });
      }
    });
  }, [elements, user, formValues, sent]);

  const clearError = (elementId) => {
    setErrors((prev) => {
      if (!(elementId in prev)) return prev;
      const { [elementId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleInputChange = (elementId, value, index) => {
    setFormValues((prevState) => {
      const newValues = { ...prevState };
      const element = elements.find((el) => el.id === elementId);
      const type = element?.type;
      const isBox = type === 'checkbox' || type === 'multiple-choice' || type === 'ticket';

      if (index !== undefined) {
        newValues[elementId] = { ...(newValues[elementId] || {}) };
        newValues[elementId][index] = isBox && newValues[elementId]?.[index] === value ? undefined : value;
      } else if (isBox && newValues[elementId] === value) {
        delete newValues[elementId];
      } else {
        newValues[elementId] = value;
      }

      return sanitizeValues(newValues);
    });
    clearError(elementId);
    setShowErrors(false);
  };

  const handleTicketQuantityChange = (elementId, optionIndex, change) => {
    setFormValues((prevState) => {
      const newValues = { ...prevState };
      newValues[elementId] = { ...(newValues[elementId] || {}) };
      const currentValue = newValues[elementId][optionIndex] || 0;
      const newValue = Math.max(0, currentValue + change);

      const element = elements.find((el) => el.id === elementId);
      const option = element?.content?.options?.[optionIndex];
      if (option?.limit && newValue > option.limit) {
        return prevState;
      }

      newValues[elementId][optionIndex] = newValue;
      return sanitizeValues(newValues);
    });
    clearError(elementId);
    setShowErrors(false);
  };

  const handleFileInput = (elementId, file) => {
    if (!file) return;
    const reader = new FileReader();
    setUploadState((prev) => ({ ...prev, [elementId]: 'uploading' }));
    reader.onload = () => {
      setFormImagesValues((prev) => ({ ...prev, [elementId]: reader.result }));
      setUploadState((prev) => ({ ...prev, [elementId]: 'uploaded' }));
    };
    reader.onerror = () => {
      setUploadState((prev) => ({ ...prev, [elementId]: 'error' }));
    };
    reader.readAsDataURL(file);
    clearError(elementId);
    setShowErrors(false);
  };

  const deleteImage = (elementId) => {
    fetchAPI(`/delete_form_image/${evId}/${elementId}/${user}`, () => {
      setFormImagesValues((prev) => ({ ...prev, [elementId]: undefined }));
      setUploadState((prev) => ({ ...prev, [elementId]: undefined }));
    }, false, false);
  };

  const handleCopiedText = (elementId, text) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedText((prev) => ({ ...prev, [elementId]: true }));
      setTimeout(() => {
        setCopiedText((prev) => ({ ...prev, [elementId]: false }));
      }, 1000);
    }
  };

  const renderTicketAvailability = (remaining, quantity = 0) => {
    if (remaining === undefined || remaining === Infinity) return 'Carregant...';
    if (remaining <= 0 && quantity === 0) return 'Ja no en queden';
    if (remaining <= 0 && quantity > 0) return "Agafes l'últim!";
    if (remaining === 1) return 'En queda un!';
    if (remaining > 10) return '';
    return `En queden ${remaining}`;
  };

  const renderElement = (element) => {
    const { id, type, content, required } = element;
    const { question, options, text } = content || {};

    if (!isElementVisible(element)) return null;

    const errorMessage = errors[id];

    switch (type) {
      case 'multiple-choice':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(isAutoPaymentMethod(element)
              ? webPaymentMethodOptions(element, formValues?.[id])
              : (options || []).map((label, index) => ({ label, index }))
            ).map(({ label: option, index }) => {
              const checked = formValues?.[id] === index;
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={id}
                    checked={checked || false}
                    onChange={() => (formDisabled ? undefined : handleInputChange(id, index))}
                    disabled={formDisabled}
                  />
                  <div>{option}</div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'checkbox':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(options || []).map((option, index) => {
              const checked = formValues?.[id]?.[index] || false;
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => (formDisabled ? undefined : handleInputChange(id, !checked, index))}
                    disabled={formDisabled}
                  />
                  <div>{option}</div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'short-answer':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <input
              type="text"
              value={formValues?.[id] || ''}
              onChange={(event) => (formDisabled ? undefined : handleInputChange(id, event.target.value))}
              disabled={formDisabled}
              placeholder="Escriu aquí la resposta..."
            />
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'paragraph':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <textarea
              value={formValues?.[id] || ''}
              onChange={(event) => (formDisabled ? undefined : handleInputChange(id, event.target.value))}
              disabled={formDisabled}
              placeholder="Escriu aquí la resposta..."
            />
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'image':
        return (
          <div key={id} className="form-element-container image-element">
            {staticImages?.[id] && <img src={staticImages[id]} alt="Form" />}
          </div>
        );
      case 'copiable-text':
        return (
          <div key={id} className="form-element-container">
            <h3 style={{ margin: 5 }}>{question}</h3>
            <input type="text" value={formatText(text)} readOnly style={{ backgroundColor: 'rgb(203 203 255)' }} />
            <button type="button" onClick={() => handleCopiedText(id, formatText(text))} className="editable-form-copy-button">
              {copiedText?.[id] ? 'Copiat!' : 'Copia el text'}
            </button>
          </div>
        );
      case 'image-upload':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {formImagesValues?.[id] && (
              <div className="image-preview">
                <img src={formImagesValues[id]} alt="Pujada" />
                <button type="button" className="editable-form-delete-image-button" onClick={() => deleteImage(id)} disabled={formDisabled}>
                  Esborra la imatge
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              aria-label={question}
              onChange={(event) => handleFileInput(id, event.target.files?.[0])}
              disabled={formDisabled}
            />
            {
              uploadState?.[id] === 'uploading' && uploadState?.[id] === 'error' && (
                <div className="upload-status">
                  {uploadState?.[id] === 'uploading' && 'Pujant imatge...'}
                  {uploadState?.[id] === 'error' && 'Error penjant la imatge'}
                </div>
              )
            }
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'text':
        return (
          <div key={id} className="form-element-container">
            <div className="rich-text" dangerouslySetInnerHTML={{ __html: formatRichText(content) }} />
          </div>
        );
      case 'resum-pagaments':
        if (totalPriceToPay === 0) {
          return (
            <div key={id} className="form-element-container payment-summary">
              <div className="payment-summary-header payment-summary-free">
                <span>No hi ha res a pagar</span>
                <span>✅</span>
              </div>
            </div>
          );
        }
        return (
          <div key={id} className="form-element-container payment-summary">
            <div className="payment-summary-header">
              <span>Total a pagar</span>
              <span>{formatMoney(totalPriceToPay)}€</span>
            </div>
            {visibleFormTickets.map((ticket) => {
              if (ticket.type === 'ticket' && formValues?.[ticket.id] === 0) {
                return (
                  <div key={`${id}-${ticket.id}`} className="payment-summary-row">
                    <span>{ticket.content.question}</span>
                    <span>{formatPriceDisplay(ticket.price)}</span>
                  </div>
                );
              }
              if (ticket.type === 'ticket-v2' || ticket.type === 'ticket-v2-single') {
                return ticket.content.options.map((option, optionIndex) => {
                  if (option.hidden) return null;
                  const quantity = formValues?.[ticket.id]?.[optionIndex] || 0;
                  if (quantity > 0 && Number(option.price) > 0) {
                    return (
                      <div key={`${ticket.id}-${optionIndex}`} className="payment-summary-row">
                        <span>
                          {quantity >= 2 && <strong className="payment-summary-quantity">x{quantity}</strong>}
                          {option.text}
                        </span>
                        <span>{formatMoney(quantity * Number(option.price))}€</span>
                      </div>
                    );
                  }
                  return null;
                });
              }
              return null;
            })}
          </div>
        );
      case 'assistance':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            <label className={`input-option ${formValues?.[id] === 1 ? 'selected' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="radio"
                name={id}
                checked={formValues?.[id] === 1}
                onChange={() => handleInputChange(id, 1)}
                disabled={formDisabled}
              />
              <span>{element.content?.yesLabel || 'Sí'}</span>
            </label>
            <label className={`input-option ${formValues?.[id] === 0 ? 'selected' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="radio"
                name={id}
                checked={formValues?.[id] === 0}
                onChange={() => handleInputChange(id, 0)}
                disabled={formDisabled}
              />
              <span>{element.content?.noLabel || 'No'}</span>
            </label>
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(options || []).map((option, index) => {
              const checked = formValues?.[id] === index;
              const optionId = `${id}_0`;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, checked ? 1 : 0);
              const disabled = formDisabled || (index === 0 && element.limit && remaining <= 0 && !checked);
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name={id}
                    checked={checked || false}
                    onChange={() => (disabled ? undefined : handleInputChange(id, index))}
                    disabled={disabled}
                  />
                  <div className="ticket-option">
                    <span>{option}</span>
                    {index === 0 && element.limit && (
                      <span className="ticket-availability">{availability}</span>
                    )}
                  </div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket-v2':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(content?.options || []).map((option, index) => {
              if (option.hidden) return null;
              const optionId = `${id}_${index}`;
              const quantity = formValues?.[id]?.[index] || 0;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, quantity);
              const disableIncrease = formDisabled || remaining <= 0;
              const disableDecrease = formDisabled || quantity <= 0;
              return (
                <div key={index} className="ticket-v2-row">
                  <div className="ticket-v2-info">
                    <span>{option.text}</span>
                    {option.limit && availability && (
                      <span className="ticket-availability">{availability}</span>
                    )}
                  </div>
                  <div className="ticket-v2-actions">
                    <div className="ticket-quantity">
                      <button
                        type="button"
                        className="editable-form-quantity-button"
                        disabled={disableDecrease}
                        onClick={() => handleTicketQuantityChange(id, index, -1)}
                        style={{
                          background: disableDecrease ? '#e0e0e0' : '#77A9C5',
                          color: disableDecrease ? '#999' : '#fff',
                        }}
                      >
                        -
                      </button>
                      <span>{quantity}</span>
                      <button
                        type="button"
                        className="editable-form-quantity-button"
                        disabled={disableIncrease}
                        onClick={() => handleTicketQuantityChange(id, index, 1)}
                        style={{
                          background: disableIncrease ? '#e0e0e0' : '#77A9C5',
                          color: disableIncrease ? '#fff' : '#fff',
                        }}
                      >
                        +
                      </button>
                    </div>
                    {!Number.isNaN(Number(option.price)) && (
                      <span className="ticket-price">{formatPriceDisplay(option.price)}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      case 'ticket-v2-single':
        return (
          <div key={id} className="form-element-container">
            <div className="form-question-header">
              <h2>{question}</h2>
              {required && <span className="form-required">*</span>}
            </div>
            {(content?.options || []).map((option, index) => {
              if (option.hidden) return null;
              const optionId = `${id}_${index}`;
              const quantity = formValues?.[id]?.[index] || 0;
              const remaining = remainingTickets?.[optionId];
              const availability = renderTicketAvailability(remaining, quantity);
              const checked = quantity === 1;
              const disabled = formDisabled || (option.limit && remaining <= 0 && !checked);
              return (
                <label key={index} className={`input-option ${checked ? 'selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleTicketQuantityChange(id, index, checked ? -1 : 1)}
                    disabled={disabled}
                  />
                  <div className="ticket-v2-single-option">
                    <div>
                      <div>{option.text}</div>
                      {option.limit && availability && (
                        <span className="ticket-availability">{availability}</span>
                      )}
                    </div>
                    {!Number.isNaN(Number(option.price)) && (
                      <div className={`ticket-price-badge ${checked ? 'selected' : ''}`}>
                        {formatPriceDisplay(option.price)}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
            {errorMessage && <div className="form-error-text">{errorMessage}</div>}
          </div>
        );
      default:
        return null;
    }
  };

  const validateForm = () => {
    const requiredElements = getRequiredVisibleElements(formData, formValues, totalPriceToPay);

    const validationErrors = {};
    const filledFormValues = Object.fromEntries(Object.entries(formValues).filter(([, value]) => value !== undefined));

    requiredElements.forEach((element) => {
      const { id, type } = element;
      switch (type) {
        case 'multiple-choice':
        case 'ticket':
          if (!(id in filledFormValues) || filledFormValues[id] === undefined) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'assistance':
          if (!(id in filledFormValues) || filledFormValues[id] === undefined) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'checkbox':
          if (!(id in filledFormValues)) {
            validationErrors[id] = 'Aquest camp és obligatori';
          } else {
            const checked = Object.values(formValues[id] || {}).some((value) => value === true);
            if (!checked) validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'image-upload':
          if (uploadState[id] === 'uploading') {
            validationErrors[id] = "Espera que s'acabi de pujar la imatge";
          } else if (uploadState[id] !== 'uploaded') {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'short-answer':
        case 'paragraph':
          if (!(id in filledFormValues) || !String(formValues[id] || '').trim()) {
            validationErrors[id] = 'Aquest camp és obligatori';
          }
          break;
        case 'ticket-v2-single':
        case 'ticket-v2':
          if (!(id in filledFormValues)) {
            validationErrors[id] = 'Aquest camp és obligatori';
          } else {
            const quantities = Object.values(formValues[id] || {});
            const totalQuantity = quantities.reduce((sum, quantity) => sum + (quantity || 0), 0);
            if (totalQuantity === 0) {
              validationErrors[id] = 'Has de comprar almenys un ticket';
            }
          }
          break;
        default:
          break;
      }
    });

    return validationErrors;
  };

  const uploadImagesIfNeeded = async (imageIds) => {
    for (const imgId of imageIds) {
      const base64 = formImagesValues[imgId];
      if (!base64) continue;
      setUploadState((prev) => ({ ...prev, [imgId]: 'uploading' }));
      try {
        // Wrap postAPI in a Promise to properly wait for the callback
        await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('Upload timeout'));
          }, 60000); // 60 second timeout for large images

          postAPI('/upload_form_image_user', {
            formId: evId,
            elementId: imgId,
            userId: user,
            base64,
          }, (response) => {
            clearTimeout(timeoutId);
            if (response?.success) {
              resolve(response);
            } else {
              reject(new Error(response?.error || 'Upload failed'));
            }
          });
        });
        setUploadState((prev) => ({ ...prev, [imgId]: 'uploaded' }));
      } catch (error) {
        setUploadState((prev) => ({ ...prev, [imgId]: 'error' }));
        throw error;
      }
    }
  };

  const submitForm = async () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setShowErrors(true);
      return;
    }

    setIsSubmitting(true);

    const cleanedFormValues = createSubmitFormValues(formData, formValues);

    const sentImages = Object.keys(formImagesValues)
      .filter((key) => formImagesValues[key])
      .filter((key) => {
        const element = elements.find((el) => el.id === key);
        return element ? isElementVisible(element) : false;
      });

    try {
      await uploadImagesIfNeeded(sentImages);
    } catch (error) {
      alert('No s\'ha pogut pujar la imatge. Torna-ho a provar.');
      setIsSubmitting(false);
      return;
    }

    const guestInfo = {
      [user]: {
        nom: mote !== '' ? mote : 'VISITANT',
        cognom: mote !== '' ? mote : 'VISITANT',
        mote: mote !== '' ? mote : 'VISITANT',
      },
      ...castellersInfo,
    };

    try {
      await new Promise((resolve, reject) => {
        postAPI('/submit_form', {
          data: cleanedFormValues,
          userId: user,
          selectedEvent: evId,
          castellersInfo: isLoggedIn ? castellersInfo : guestInfo,
          formData,
          sentImages,
        }, (response) => {
          if (response?.success) {
            resolve(response);
          } else {
            reject(new Error(response?.error || 'Submit failed'));
          }
        }, reject);
      });
    } catch (error) {
      alert('No s\'ha pogut enviar el formulari. Torna-ho a provar.');
      setIsSubmitting(false);
      return;
    }

    setFormValues(cleanedFormValues);
    setSent(true);
    setShowErrors(false);
    setIsSubmitting(false);
  };

  const elementsOrdered = useMemo(() => {
    if (!formData?.order || !Array.isArray(formData.order)) return elements;
    return [
      ...formData.order
        .map((elementId) => elements.find((element) => element.id === elementId))
        .filter(Boolean),
      ...elements.filter((element) => !formData.order.includes(element.id)),
    ];
  }, [elements, formData?.order]);

  return sent ? (
    <div className="form-container">
      <div className="editable-form-confirmation-container">
        <h2>S'ha enviat correctament!</h2>
        <p>{mote}, gràcies per enviar la resposta.</p>

        <div className="editable-form-button-container">
          <button
            onClick={() => {
              setIsLoggedIn(false);
              setSent(false);
              setFormValues({});
              setMote('');
            }}
            className="editable-form-button editable-form-button-reset"
          >
            Fes-ne una altra
          </button>

          <button
            onClick={() => {
              setSent(false);
            }}
            className="editable-form-button editable-form-button-secondary"
          >
            Modifica la resposta
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className="form-container">
      <div>
        <h1>{formData.title}</h1>
        <p dangerouslySetInnerHTML={{ __html: formatRichText(formData.description || '') }} />

        {!isLoggedIn && !isWarningAccepted && (
          <div className="form-warning">
            <em>
              Respon millor aquest formulari des de l'app si ja tens usuari, així evitem duplicats!
            </em>
          </div>
        )}

        {!isLoggedIn && !isWarningAccepted && (
          <div className="form-element-container">
            <div className="warning-container">
              <button
                onClick={() => setIsWarningAccepted(true)}
                className="editable-form-button"
              >
                No tinc l'app, i ho he de respondre des de la web.
              </button>
            </div>
          </div>
        )}

        {(isLoggedIn || isWarningAccepted) && (
          <div className="form-element-container">
            <label>
              <div>El teu nom {isLoggedIn ? ` (estàs connectat com a tècnica/junta)` : ''}</div>
              <input
                type="text"
                value={mote}
                onChange={(event) => setMote(event.target.value)}
                disabled={isLoggedIn}
                placeholder="Diga'ns qui ets..."
                style={{ fontSize: 14 }}
              />
            </label>
            
            {isLoggedIn && (
              <button
                onClick={() => {
                  setIsLoggedIn(false);
                  setMote('');
                }}
                className="editable-form-button editable-form-button-secondary"
                style={{
                  marginTop: '10px'
                }}
              >
                Contestes per una altra persona?
              </button>
            )}
          </div>
        )}

        {formDisabled && (
          <div className="form-status-message">
            {formData?.hidden && '(Aquest formulari està ocult) '}
            {outdated && `El formulari va tancar el ${moment(formData?.closingDate).format('DD [de] MMM')} a les ${moment(formData?.closingDate).format('HH:mm')}`}
            {notOpenYet && `El formulari s'obrirà el ${moment(formData?.openingDate).format('DD [de] MMM')} a les ${moment(formData?.openingDate).format('HH:mm')}`}
          </div>
        )}

        {closeToEnding && (
          <div className="form-status-message">
            El formulari tancarà el {moment(formData?.closingDate).format('DD [de] MMM')} a les {moment(formData?.closingDate).format('HH:mm')} (En {
              moment(formData?.closingDate).diff(moment(), 'hours') < 1
                ? `${moment(formData?.closingDate).diff(moment(), 'minutes')} minuts`
                : moment(formData?.closingDate).diff(moment(), 'days') < 1
                ? `${moment(formData?.closingDate).diff(moment(), 'hours')} hores`
                : `${moment(formData?.closingDate).diff(moment(), 'days')} dies`
            })
          </div>
        )}

        {showErrors && Object.keys(errors).length > 0 && (
          <div className="form-error-summary">
            Hi ha camps obligatoris sense omplir. Revisa les preguntes marcades en vermell.
          </div>
        )}

        {(isLoggedIn || isWarningAccepted) && (
          <div style={formData?.hidden ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            {elementsOrdered.map((element) => renderElement(element))}

            <div className="form-element-container">
              <button
                onClick={formDisabled ? undefined : submitForm}
                disabled={formDisabled || isSubmitting}
                className="editable-form-button"
              >
                {formDisabled
                  ? outdated
                    ? 'Formulari tancat'
                    : notOpenYet
                    ? 'Formulari no obert'
                    : 'Formulari ocult'
                  : formSubmitted === false
                  ? isSubmitting ? 'Enviant...' : 'Enviar'
                  : isSubmitting ? 'Enviant...' : 'Modificar respostes'}
              </button>

              {!formDisabled && (
                <button
                  onClick={() => {
                    setIsLoggedIn(false);
                    setSent(false);
                    setFormValues({});
                    setMote('');
                    window.scrollTo(0, 0);
                  }}
                  className="editable-form-button editable-form-button-reset"
                >
                  Fes-ne una altra
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableForm;
